// src/pages/CourseDetailPage.jsx
import '@mux/mux-player';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import AssignmentSetup from '../components/assignments/AssignmentSetup';
import UserAssignmentsTable from '../components/assignments/UserAssignmentsTable';
import { FloatingActionButton, SectionHeader } from '../components/common';
import CourseDetailHero from '../components/courseDetail/CourseDetailHero';
import CourseDetailTabs from '../components/courseDetail/CourseDetailTabs';
import LearnerCourseDetailLayout from '../components/courseDetail/LearnerCourseDetailLayout';
import LessonListSidebar from '../components/courseDetail/LessonListSidebar';
import { getDisplayModuleNames } from '../utils/coursePrefs';
import { getGateLockedLessonIds } from '../utils/lessonGates';
import { orderLessonsByCurriculum } from '../utils/lessonOrder';
import LessonViewer from '../components/courseDetail/LessonViewer';
import { ManagerViewIndicator } from '../components/courseDetail/ManagerViewIndicator';
import TabNavigation, { CourseDetailTabKey } from '../components/courseDetail/TabNavigation';
import CourseEnrollCta from '../components/courseDetail/CourseEnrollCta';
import ExternalCourseSection from '../components/courseDetail/ExternalCourseSection';
import EventVerificationPanel from '../components/courseDetail/EventVerificationPanel';
import CourseCertificatePanel from '../components/courseDetail/CourseCertificatePanel';
import LearningPathSection from '../components/courseDetail/LearningPathSection';
import StandardLessonsSection from '../components/courseDetail/StandardLessonsSection';
import ConfirmDialog from '../components/ConfirmDialog';
import EditLessonDialog from '../components/EditLesson';
import EditModuleDialog from '../components/EditModule';
import { BookmarkIcon, CreateNewFolderIcon, LockOutlinedIcon } from '../components/icons';
import { Box, Button, CircularProgress, Typography } from '../components/ui';
import { DownloadIcon } from '../components/icons';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { useCourseManagement } from '../hooks/useCourseManagement';
import { useLessonManagement } from '../hooks/useLessonManagement';
import { useModuleManagement } from '../hooks/useModuleManagement';
import { useVideoPlayback } from '../hooks/useVideoPlayback';
import { certificatesClient, coursesClient, enrollmentsClient, lessonCompletionsClient } from '../apis/client-instances';
import {
  codeMatches,
  EventVerificationConfig,
  getEventVerification,
  hasLearnerVerification,
} from '../utils/eventVerification';
import { alert, getFileUrl } from '../hooks/function';
import { RootState } from '../types';
import { Certificate, CourseType, EnrollmentStatus, LessonType } from '../apis/api-client';

export default function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we came from a learning path
  const fromLearningPath = (location.state as { fromLearningPath?: string } | null)?.fromLearningPath;
  // Generic "return here" state — set by callers like /reports → entity-link
  // so the topbar back button can return to the originating tab/filters.
  const navFrom = (location.state as { from?: string } | null)?.from;
  const navFromText = (location.state as { fromText?: string } | null)?.fromText;
  // Default to Overview; the effect below flips to Curriculum once we know the
  // user is enrolled. Anonymous and not-yet-enrolled users stay on Overview.
  const [selectedTab, setSelectedTab] = useState<CourseDetailTabKey>('overview');
  const tabInitializedRef = useRef(false);
  const { setLayoutConfig } = useLayoutConfig();
  const currentUser = useSelector((state: RootState) => state.profile.user);

  // Check if user is authenticated
  const isAuthenticated = !!currentUser;
  const { enqueueSnackbar } = useSnackbar();

  // State for slug-based course fetching. The slug endpoint is anonymous-safe
  // for public courses, so this is the single entry point for both auth and
  // unauth visitors. Auth users additionally hydrate via useCourseManagement
  // below (for enrollment enrichments, etc.).
  const [slugCourse, setSlugCourse] = useState<any>(null);
  const [slugCourseLoading, setSlugCourseLoading] = useState(true);
  const [courseIdFromSlug, setCourseIdFromSlug] = useState<number | null>(null);

  useEffect(() => {
    const fetchCourseBySlug = async () => {
      if (!slug) return;

      try {
        setSlugCourseLoading(true);
        const course = await coursesClient.getCourseBySlug(slug);
        setSlugCourse(course);
        setCourseIdFromSlug(course.id!);
      } catch (error) {
        console.error('Failed to fetch course:', error);
        setSlugCourse(null);
      } finally {
        setSlugCourseLoading(false);
      }
    };

    fetchCourseBySlug();
  }, [slug]);

  // Course ID from slug lookup
  const effectiveCourseId = courseIdFromSlug;

  // Custom hooks handle all complexity (only run when we have a valid course ID)
  const courseManagement = useCourseManagement(effectiveCourseId || 0);
  const lessonManagement = useLessonManagement(effectiveCourseId || 0);
  const moduleManagement = useModuleManagement(effectiveCourseId || 0);
  const { videoPlayback, loading: videoLoading } = useVideoPlayback(lessonManagement.selectedLesson);

  // External course state
  const [externalLoading, setExternalLoading] = useState(false);
  // Event verification — per-slug admin config plus the learner's
  // in-progress attempt at satisfying it. Re-hydrated when the slug
  // resolves so a deep-linked event page lands on the right config
  // without a flash of the legacy "Open Event marks complete" UI.
  const [verification, setVerification] = useState<EventVerificationConfig | null>(null);
  const [codeAttempt, setCodeAttempt] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Certificate state
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [certificateGenerating, setCertificateGenerating] = useState(false);
  const [certificateJustGenerated, setCertificateJustGenerated] = useState(false);

  // Lesson-just-completed state — drives the inline "Lesson complete"
  // overlay over the LessonViewer. Set inside handleLessonComplete /
  // onQuizPassed when a fresh completion lands; cleared when the
  // learner advances, dismisses, or navigates to a different lesson.
  const [completedLessonForOverlay, setCompletedLessonForOverlay] = useState<number | null>(null);
  // Clear the overlay when the learner manually navigates to a
  // different lesson — they've moved on, so the previous lesson's
  // completion card shouldn't follow them.
  useEffect(() => {
    setCompletedLessonForOverlay(null);
  }, [lessonManagement.selectedLesson?.id]);

  // Next-lesson lookup powering the overlay's "Next: …" CTA. Returns
  // null on the last lesson so the overlay can render the
  // course-complete variant instead.
  // The overlay's next-lesson CTA — and, by its absence, the "course
  // complete" variant. Point at the next *incomplete* lesson (in curriculum
  // order, grouped by module — not raw fetch order) so finishing the
  // last-positioned lesson while earlier ones are still unfinished sends the
  // learner to what's left instead of falsely declaring the course done.
  // Only when no incomplete lesson remains does this return null and let the
  // overlay celebrate completion.
  const overlayNextLesson = (() => {
    const currentId = lessonManagement.selectedLesson?.id;
    if (currentId == null) return null;
    const ordered = orderLessonsByCurriculum(lessonManagement.lessons, moduleManagement.modules);
    // Count the just-finished lesson as done even if the completed-ids
    // refresh hasn't landed yet.
    const completed = new Set(lessonManagement.completedLessonIds);
    completed.add(currentId);
    const idx = ordered.findIndex((l: any) => l.id === currentId);
    const nextIncomplete =
      ordered.slice(idx + 1).find((l: any) => !completed.has(l.id)) ??
      ordered.find((l: any) => !completed.has(l.id));
    return nextIncomplete
      ? { id: nextIncomplete.id, title: nextIncomplete.title || 'Next lesson' }
      : null;
  })();

  const advanceToNextLesson = useCallback(() => {
    setCompletedLessonForOverlay(null);
    if (overlayNextLesson) {
      const full = lessonManagement.lessons.find((l: any) => l.id === overlayNextLesson.id);
      if (full) lessonManagement.setSelectedLesson(full);
    }
  }, [overlayNextLesson, lessonManagement.lessons, lessonManagement.setSelectedLesson]);

  const dismissCompletionOverlay = useCallback(() => {
    setCompletedLessonForOverlay(null);
  }, []);

  // Completion overlay is video-only. A modal celebrating "lesson complete" +
  // "Next" reads naturally when a video ends, but for text/doc/quiz — where
  // completion is a quiet dwell timer or a submit — it pops up unprompted and
  // feels jarring. Those still mark complete silently (sidebar check + progress
  // update); the learner advances via the curriculum list.
  const showCompletionOverlay =
    completedLessonForOverlay != null &&
    completedLessonForOverlay === lessonManagement.selectedLesson?.id &&
    lessonManagement.selectedLesson?.lessonType === LessonType.Video;

  // Enrollment state checks
  const isEnrolled = courseManagement.course?.currentUserEnrollment != null;
  const shouldShowLessonViewer = isEnrolled || courseManagement.canEdit;
  const showManagerIndicator = courseManagement.canEdit && !isEnrolled && !courseManagement.course?.isDraft;
  // Locked curriculum view for users who aren't enrolled (anon OR authed-not-enrolled).
  // Editors always see full curriculum unlocked.
  const curriculumLocked = !isEnrolled && !courseManagement.canEdit;

  // Lock enforcement on the player itself. The sidebar already greys out and
  // blocks clicks on locked lessons, but we guard the viewer too so a locked
  // lesson can't be reached via deep-link or auto-selection. Both mechanisms
  // apply, matching the sidebar's isLessonLocked: a per-lesson gate (locked
  // until all preceding lessons are complete) OR course-wide linear progress
  // (locked until the immediately-preceding lesson is complete). Editors
  // bypass. Curriculum order = ungrouped lessons, then each module's lessons.
  const selectedLessonLocked = (() => {
    if (courseManagement.canEdit) return false;
    const sel = lessonManagement.selectedLesson;
    if (!sel) return false;
    const ungrouped = lessonManagement.lessons.filter((l: any) => l.moduleId == null);
    const byModule = moduleManagement.modules.flatMap((m: any) =>
      lessonManagement.lessons.filter((l: any) => l.moduleId === m.id),
    );
    const orderedLessons = [...ungrouped, ...byModule];
    if (getGateLockedLessonIds(orderedLessons, lessonManagement.completedLessonIds).has(String(sel.id))) {
      return true;
    }
    if (courseManagement.course?.enforceLinearProgress) {
      const idx = orderedLessons.findIndex((l: any) => l.id === sel.id);
      if (idx > 0 && !lessonManagement.completedLessonIds.includes(orderedLessons[idx - 1]?.id)) {
        return true;
      }
    }
    return false;
  })();

  // Shared "this lesson is locked" panel — rendered in place of the
  // lesson viewer when the selected lesson is gated. Mirrors the grey
  // placeholder styling used for the not-yet-started states below.
  const lockedLessonPanel = (
    <Box
      sx={{
        flex: 1,
        minWidth: 400,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: 6,
        borderRadius: '12px',
        border: 'solid 1px rgb(228, 228, 228)',
        bgcolor: 'grey.50',
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 56, color: 'grey.400', mb: 2 }} />
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Lesson locked
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 460 }}>
        Complete the earlier lessons in this course to unlock this one.
      </Typography>
    </Box>
  );

  // Enrollment handlers
  const handleEnroll = async () => {
    try {
      await enrollmentsClient.createEnrollment({
        userId: currentUser?.id,
        courseId: effectiveCourseId!,
      });
      alert(enqueueSnackbar, 'Successfully enrolled in course', 'success');
      // Refresh course data to get enrollment
      await courseManagement.fetchCourse();
      // Drop them into the curriculum now that it's unlocked.
      setSelectedTab('lessons');
    } catch (error) {
      console.error('Failed to enroll in course:', error);
      alert(enqueueSnackbar, 'Failed to enroll in course', 'error');
    }
  };

  const handlePublish = async () => {
    try {
      await coursesClient.updateCourse(effectiveCourseId!, {
        title: courseManagement.course?.title,
        description: courseManagement.course?.description,
        thumbnail: courseManagement.course?.thumbnail,
        tagIds: courseManagement.course?.tags?.map((tag: any) => tag.id) || [],
        isDraft: false,
      });
      alert(enqueueSnackbar, 'Course published successfully', 'success');
      // Refresh course data
      await courseManagement.fetchCourse();
    } catch (error) {
      console.error('Failed to publish course:', error);
      alert(enqueueSnackbar, 'Failed to publish course', 'error');
    }
  };

  const handleLessonComplete = useCallback(
    async (lessonId: number) => {
      // Check if lesson already completed - prevent duplicate API calls
      if (lessonManagement.completedLessonIds.includes(lessonId)) {
        console.log(`Lesson ${lessonId} already completed - skipping duplicate completion`);
        return;
      }

      let enrollmentId = (courseManagement.course as any)?.currentUserEnrollment?.enrollmentId;

      // Fallback: Auto-start if assigned but not enrolled yet
      if (!enrollmentId && courseManagement.course?.currentUserEnrollment?.isAssigned) {
        try {
          const enrollment = await enrollmentsClient.startCourse({
            userId: currentUser?.id,
            courseId: effectiveCourseId!,
          });
          enrollmentId = enrollment.enrollmentId;
        } catch (error) {
          console.error('Failed to start course:', error);
          alert(enqueueSnackbar, 'Failed to start course', 'error');
          return;
        }
      }

      // If still no enrollmentId and not assigned, skip
      if (!enrollmentId) {
        console.log('Skipping completion - not enrolled or assigned');
        return;
      }

      try {
        // Mark lesson complete (idempotent - safe to call multiple times)
        await lessonCompletionsClient.markLessonComplete({
          enrollmentId,
          lessonId,
        });

        console.log(`Lesson ${lessonId} marked as complete`);

        // Trigger the inline completion overlay. Scoped to this exact
        // lesson id so navigating away (or completing a different
        // lesson) doesn't leave a stale overlay behind.
        setCompletedLessonForOverlay(lessonId);

        // Refresh course data to update progress percentage
        await courseManagement.fetchCourse();

        // Refresh completed lessons to update sidebar UI
        lessonManagement.fetchCompletedLessons(enrollmentId);
      } catch (error) {
        console.error('Failed to mark lesson complete:', error);
        // Silent failure - don't disrupt UX
      }
    },
    [
      lessonManagement.completedLessonIds,
      lessonManagement.fetchCompletedLessons,
      courseManagement.course,
      courseManagement.fetchCourse,
      currentUser?.id,
      effectiveCourseId,
      enqueueSnackbar,
    ]
  );

  // Auto-start course for assigned users
  useEffect(() => {
    const autoStartIfAssigned = async () => {
      // If user is assigned but hasn't started the course yet
      if (courseManagement.course?.currentUserEnrollment?.isAssigned && !courseManagement.course?.currentUserEnrollment?.enrollmentId && effectiveCourseId) {
        try {
          await enrollmentsClient.startCourse({
            userId: currentUser?.id,
            courseId: effectiveCourseId,
          });
          // Refresh course data to get the new enrollmentId
          await courseManagement.fetchCourse();
        } catch (error) {
          console.error('Failed to auto-start course:', error);
        }
      }
    };

    if (courseManagement.course && currentUser) {
      autoStartIfAssigned();
    }
  }, [courseManagement.course?.id, currentUser?.id, effectiveCourseId]);

  // Set layout configuration for this page
  useEffect(() => {
    if (navFrom) {
      setLayoutConfig({
        showBackButton: true,
        backButtonText: navFromText ?? 'Back',
        backButtonAction: () => navigate(navFrom),
      });
    } else if (fromLearningPath) {
      setLayoutConfig({
        showBackButton: true,
        backButtonText: 'Back to Learning Path',
        backButtonAction: () => navigate(`/courses/${fromLearningPath}`),
      });
    } else {
      setLayoutConfig({
        showBackButton: true,
        backButtonText: 'Back to Courses',
        backButtonAction: () => navigate('/courses'),
      });
    }
  }, [setLayoutConfig, navigate, fromLearningPath, navFrom, navFromText]);

  // Fetch the course when we have a valid course ID. The curriculum (modules +
  // lessons) rides along on the course detail payload, so we no longer fire
  // separate module/lesson fetches here — that's what let them race and land
  // the learner on the wrong lesson. They're seeded from the course below.
  useEffect(() => {
    if (effectiveCourseId && effectiveCourseId > 0) {
      courseManagement.fetchCourse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCourseId]);

  // Seed modules + lessons from the course payload the moment it lands, so the
  // player has the whole curriculum at once instead of two racing fetches.
  // Re-runs only when the course object itself changes (e.g. after a refetch on
  // publish/save) — admin-side add/edit/reorder mutate the hook state directly
  // and don't touch the course, so they aren't clobbered.
  //
  // Fallback: a server that predates the inline-curriculum change omits these
  // fields entirely (undefined, not an empty array). In that window we fall
  // back to the standalone fetches so the page still renders — the helper-based
  // auto-select tolerates the resulting fetch race.
  useEffect(() => {
    const course = courseManagement.course;
    if (!course) return;
    if (course.modules !== undefined || course.lessons !== undefined) {
      moduleManagement.seedModules(course.modules ?? []);
      lessonManagement.seedLessons(course.lessons ?? []);
    } else {
      moduleManagement.fetchModules();
      lessonManagement.fetchLessons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseManagement.course]);

  // Pick the default tab once we know the user's enrollment state. Enrolled
  // users get dropped straight into the curriculum; everyone else (anon,
  // not-enrolled, managers) lands on the marketing Overview.
  useEffect(() => {
    if (tabInitializedRef.current) return;
    if (slugCourseLoading) return;
    if (isAuthenticated && courseManagement.loading) return;
    tabInitializedRef.current = true;
    // Used to auto-route enrolled users straight to the lessons
    // player. Retired — everyone (learners, admins, super-admins)
    // now lands on the new marketing layout (CE / instructor /
    // overview), and chooses to enter the player via the
    // Continue learning / Review lessons CTA.
  }, [slugCourseLoading, isAuthenticated, courseManagement.loading, isEnrolled]);

  // Auto-select the first lesson in curriculum order on initial load, using the
  // shared orderLessonsByCurriculum helper so this can't drift from what the
  // sidebar shows. Skip for non-enrolled viewers — curriculum is locked and the
  // lesson viewer isn't rendered. Skip if the user already picked one.
  //
  // The empty-list guard matters: lessons and modules load from independent
  // fetches, and imported lessons reset SequenceOrder to 1 inside each module,
  // so any "lowest SequenceOrder wins" pick made before modules arrive lands on
  // an arbitrary module's lesson (in practice, the end-of-course quiz). When
  // every lesson is grouped, the helper returns [] until modules load, so we
  // wait for them rather than mis-selecting and latching it in via the guard above.
  useEffect(() => {
    if (lessonManagement.selectedLesson) return;
    if (curriculumLocked) return;

    const firstLesson = orderLessonsByCurriculum(
      lessonManagement.lessons,
      moduleManagement.modules,
    )[0];

    if (firstLesson) lessonManagement.setSelectedLesson(firstLesson);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonManagement.lessons, moduleManagement.modules, curriculumLocked]);

  // Fetch completed lessons when enrollment exists
  useEffect(() => {
    const enrollmentId = courseManagement.course?.currentUserEnrollment?.enrollmentId;
    if (enrollmentId) {
      lessonManagement.fetchCompletedLessons(enrollmentId);
    }
  }, [courseManagement.course?.currentUserEnrollment?.enrollmentId]);

  // Fetch certificate when enrollment is completed (only for authenticated users).
  useEffect(() => {
    if (!isAuthenticated) {
      setCertificate(null);
      return;
    }
    const enrollment = courseManagement.course?.currentUserEnrollment;
    if (enrollment?.enrollmentId && enrollment?.enrollmentStatus === EnrollmentStatus.Completed) {
      certificatesClient.getCertificateByEnrollment(enrollment.enrollmentId).then(setCertificate).catch(() => setCertificate(null));
    }
  }, [isAuthenticated, courseManagement.course?.currentUserEnrollment?.enrollmentId, courseManagement.course?.currentUserEnrollment?.enrollmentStatus]);

  // courseManagement.course has richer data for authenticated users;
  // fall back to the anonymous slug fetch otherwise.
  const displayCourse = courseManagement.course || slugCourse;
  // Loading: wait for slug fetch, and for authenticated users also wait for courseManagement
  const isLoading = slugCourseLoading || (effectiveCourseId && courseManagement.loading);

  // External course checks (must be after displayCourse is defined)
  const isExternal = (displayCourse as any)?.courseType === CourseType.External;
  const externalUrl = (displayCourse as any)?.externalUrl;
  const eventDate = (displayCourse as any)?.eventDate;
  const enrollmentStatus = displayCourse?.currentUserEnrollment?.enrollmentStatus;
  const isExternalCompleted = enrollmentStatus === EnrollmentStatus.Completed;
  const displaySlug = (displayCourse as any)?.slug as string | undefined;

  // Hydrate verification config from the loaded course. Effect (not
  // inline) so the admin saving a new code while the learner page is
  // open also gets picked up when the course refetches.
  useEffect(() => {
    if (isExternal && displayCourse) {
      setVerification(getEventVerification((displayCourse as any)?.eventVerification));
    } else {
      setVerification(null);
    }
    // Reset learner attempts whenever the source event changes.
    setCodeAttempt('');
    setCodeError(null);
    setAttestationChecked(false);
  }, [isExternal, displayCourse]);

  // Convenience flags for the JSX. `learnerVerificationActive` =
  // the admin enabled at least one method the LEARNER can act on
  // (code or attestation). The disabled stubs (admin-marked / webinar
  // API) don't count here — they don't affect this page yet.
  const learnerVerificationActive =
    !!verification && hasLearnerVerification(verification);

  // Learning Path checks
  const isLearningPath = (displayCourse as any)?.courseType === CourseType.LearningPath;
  const pathCourses = (displayCourse as any)?.pathCourses || [];
  const totalPathCourses = (displayCourse as any)?.totalPathCourses || 0;
  const completedPathCourses = (displayCourse as any)?.completedPathCourses || 0;

  /**
   * Mark an external event complete via the shared enrollment endpoint
   * + refresh course state + regenerate certificate. Shared by the
   * legacy "Open Event marks complete" fallback and the new
   * verification panel — both paths end here once their precondition
   * (admin didn't configure verification / learner satisfied a
   * configured method) is met.
   */
  const markEventComplete = async (): Promise<boolean> => {
    if (!effectiveCourseId) return false;
    try {
      await enrollmentsClient.completeExternalCourse({
        userId: currentUser?.id,
        courseId: effectiveCourseId,
      });
      await courseManagement.fetchCourse();
      const enrollment = courseManagement.course?.currentUserEnrollment;
      if (enrollment?.enrollmentId) {
        certificatesClient.getCertificateByEnrollment(enrollment.enrollmentId).then(setCertificate).catch(() => {});
      }
      return true;
    } catch (error) {
      console.error('Failed to complete external course:', error);
      alert(enqueueSnackbar, 'Failed to mark event as completed', 'error');
      return false;
    }
  };

  /**
   * Legacy "Open Event" path — opens the URL AND marks complete in one
   * tap. Used only when the admin hasn't configured any learner
   * verification method (`learnerVerificationActive === false`). When
   * verification IS configured, the open button just opens the URL
   * (see `handleOpenExternalUrl`) and the verification panel handles
   * completion.
   */
  const handleOpenExternalEvent = async () => {
    if (!externalUrl) return;
    try {
      setExternalLoading(true);
      const ok = await markEventComplete();
      if (ok) alert(enqueueSnackbar, 'Event marked as completed', 'success');
    } finally {
      setExternalLoading(false);
    }
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  /** Just open the external URL — no completion. Used when verification
   *  is configured; learner marks complete by entering the code or
   *  ticking the attestation in the verification panel below. */
  const handleOpenExternalUrl = () => {
    if (!externalUrl) return;
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  /**
   * Verify the learner's submitted attempt against the admin's config.
   * OR-logic across enabled methods: any one satisfied marks complete.
   * Surfaces specific feedback (wrong code vs missing attestation) so
   * the learner knows which method to retry.
   */
  const handleVerifyAttendance = async () => {
    if (!verification) return;
    setCodeError(null);

    let satisfied = false;
    if (verification.codeEnabled && codeMatches(verification, codeAttempt)) {
      satisfied = true;
    }
    if (verification.attestationEnabled && attestationChecked) {
      satisfied = true;
    }

    if (!satisfied) {
      // Specific feedback when the only thing the learner attempted
      // was a code that didn't match.
      if (verification.codeEnabled && codeAttempt.trim()) {
        setCodeError("That code doesn't match. Check with the event host.");
      } else {
        alert(
          enqueueSnackbar,
          verification.codeEnabled && verification.attestationEnabled
            ? 'Enter the code or tick the attestation to mark complete.'
            : verification.codeEnabled
              ? 'Enter the confirmation code to mark complete.'
              : 'Tick the attestation to mark complete.',
          'warning',
        );
      }
      return;
    }

    setVerifyLoading(true);
    try {
      const ok = await markEventComplete();
      if (ok) alert(enqueueSnackbar, 'Attendance recorded — event complete', 'success');
    } finally {
      setVerifyLoading(false);
    }
  };

  const downloadCertificateFile = async (cert: Certificate) => {
    try {
      const url = await getFileUrl(cert.imageFilename);
      if (!url) return;
      const slug = (cert.courseTitle || 'course').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const name = (cert.studentName || 'student').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const filename = `certificate-${slug}-${name}.png`;
      const res = await fetch(url);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      alert(enqueueSnackbar, 'Failed to download certificate', 'error');
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificate) return;
    await downloadCertificateFile(certificate);
  };

  const handleGenerateCertificate = async () => {
    const enrollmentId = courseManagement.course?.currentUserEnrollment?.enrollmentId;
    if (!enrollmentId) return;

    setCertificateGenerating(true);
    setCertificateJustGenerated(false);
    try {
      // generateCertificate throws if it can't (e.g. no template); the
      // else branch below already handles the "no cert" outcome.
      let cert: Certificate | null = null;
      try {
        cert = await certificatesClient.generateCertificate(enrollmentId);
      } catch {
        /* handled by the else branch */
      }
      if (cert) {
        setCertificate(cert);
        setCertificateGenerating(false);
        setCertificateJustGenerated(true);
        // Auto-download
        await downloadCertificateFile(cert);
        // Show checkmark for 3 seconds then transition to download button
        setTimeout(() => setCertificateJustGenerated(false), 3000);
      } else {
        setCertificateGenerating(false);
        alert(enqueueSnackbar, 'Could not generate certificate. Ensure a certificate template is configured.', 'warning');
      }
    } catch {
      setCertificateGenerating(false);
      alert(enqueueSnackbar, 'Failed to generate certificate', 'error');
    }
  };

  if (isLoading) {
    return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            color: 'text.secondary',
          }}
        >
          <CircularProgress size={50} />
        </Box>
    );
  }

  if (!displayCourse) {
    return (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6">Course not found</Typography>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Box>
    );
  }

  // Shared curriculum nodes — the sidebar and lesson viewer are rendered
  // identically by both the standard-lessons and learning-path sections.
  // Built once here (keeping all state/async wiring in the page) and passed
  // down so the presentational sections stay controlled.
  const lessonSidebar = (
    <LessonListSidebar
      courseTitle={displayCourse?.title}
      instructorMessagingEnabled={(displayCourse as any)?.instructorMessagingEnabled}
      instructorMessagingEmail={(displayCourse as any)?.instructorMessagingEmail}
      learnerName={
        currentUser
          ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ')
          : undefined
      }
      lessons={lessonManagement.lessons}
      selectedLessonId={lessonManagement.selectedLesson?.id}
      onSelectLesson={lessonManagement.setSelectedLesson}
      enrollmentStatus={(displayCourse as any)?.currentUserEnrollment?.enrollmentStatus}
      progressPercentage={(displayCourse as any)?.currentUserEnrollment?.progressPercentage}
      completedLessonIds={lessonManagement.completedLessonIds}
      enforceLinearProgress={displayCourse?.enforceLinearProgress}
      canBypass={courseManagement.canEdit}
      onReorder={lessonManagement.reorderLessons}
      modules={moduleManagement.modules}
      onReorderModules={moduleManagement.reorderModules}
      onEditModule={moduleManagement.openEditModuleDialog}
      onDeleteModule={moduleManagement.confirmDeleteModule}
      onAddModule={moduleManagement.openAddModuleDialog}
      showModuleHeaders={getDisplayModuleNames(displayCourse)}
      locked={curriculumLocked}
    />
  );

  const lessonViewer = (
    <LessonViewer
      lesson={lessonManagement.selectedLesson}
      videoPlayback={videoPlayback}
      videoLoading={videoLoading}
      canEdit={courseManagement.canEdit}
      onEdit={lessonManagement.openEditDialog}
      onDelete={lessonManagement.confirmDelete}
      onLessonComplete={handleLessonComplete}
      enrollmentId={courseManagement.course?.currentUserEnrollment?.enrollmentId}
      showLinearProgressNotice={!!displayCourse?.enforceLinearProgress && !courseManagement.canEdit}
      showCompletionOverlay={showCompletionOverlay}
      nextLesson={overlayNextLesson}
      onAdvanceToNext={advanceToNextLesson}
      onDismissCompletion={dismissCompletionOverlay}
      onQuizPassed={async () => {
        const passedId = lessonManagement.selectedLesson?.id;
        if (passedId != null) setCompletedLessonForOverlay(passedId);
        await courseManagement.fetchCourse();
        const eid = courseManagement.course?.currentUserEnrollment?.enrollmentId;
        if (eid) lessonManagement.fetchCompletedLessons(eid);
      }}
    />
  );

  return (
      <Box sx={{ width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box elevation={0} sx={{ overflow: 'hidden', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, pb: { xs: 5, md: 6 } }}>
          {/* Blue `CourseHeader` banner retired — the course title
              and Enroll CTA now live in the LearnerCourseDetailLayout
              top band so the title isn't duplicated and the visual
              hierarchy starts with the course itself. */}

          {/* "Ask a question about this course" now lives in the lesson
              sidebar footer — contextually grouped with the curriculum
              and visible while learning, rather than floating above the
              player area. See LessonListSidebar's footer block. */}

          {/* Unauthenticated enroll CTA — persistent bar prompting sign-in */}
          {!isAuthenticated && <CourseEnrollCta onSignIn={() => navigate('/signin')} />}

          {/* Manager View Indicator - hide for external events and learning paths */}
          {showManagerIndicator && !isExternal && !isLearningPath && (
            <Box sx={{ width: '95%', maxWidth: '1500px' }}>
              <ManagerViewIndicator onEnroll={handleEnroll} />
            </Box>
          )}

          {/* Certificate CTA — surfaced once a learner has completed a
              course that issues a certificate. Visible across tabs so it
              doesn't get buried in the lesson player. The generate/download
              state machine lives in the page; this just renders the band. */}
          {isAuthenticated &&
            enrollmentStatus === EnrollmentStatus.Completed &&
            displayCourse?.issuesCertificate && (
              <Box sx={{ width: '95%', maxWidth: '1500px' }}>
                <CourseCertificatePanel
                  certificate={certificate}
                  generating={certificateGenerating}
                  justGenerated={certificateJustGenerated}
                  onGenerate={handleGenerateCertificate}
                  onDownload={handleDownloadCertificate}
                />
              </Box>
            )}

          {/* Top-level Overview / Curriculum tab strip is retired —
              the in-page learner layout has its own CE / About / Course
              Overview tabs that cover the same ground, and the
              curriculum CTA inside that layout still flips the
              underlying `selectedTab` to 'lessons' so the existing
              lesson-player branch below keeps working unchanged.
              Manager-only Assignment Setup / Assignments still need
              their own surface — only render the tab strip when those
              are applicable, and only show those tabs there. */}
          {courseManagement.canAssign && !displayCourse?.isDraft && (
            <TabNavigation
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              isExternal={isExternal}
              showManagerTabs
            />
          )}

          {/* Overview tab — single layout for everyone (learners,
              admins, super-admins). The old admin-only hero + author-
              tabs branch was retired so the detail page reads the same
              way for the people building the course and the people
              taking it. Admins still get the "Assign instructor →"
              shortcut via `editLink` so the page can fast-track them
              to settings when content is missing. */}
          {selectedTab === 'overview' && (
            <Box sx={{ width: '95%', maxWidth: '1500px' }}>
              <LearnerCourseDetailLayout
                course={displayCourse}
                thumbnailDisplayUrl={courseManagement.thumbnailDisplayUrl}
                modules={moduleManagement.modules}
                lessons={lessonManagement.lessons}
                lessonCount={lessonManagement.lessons.length || undefined}
                completedCount={lessonManagement.completedLessonIds?.length}
                ctaLabel={
                  isAuthenticated
                    ? enrollmentStatus === EnrollmentStatus.Completed
                      ? 'Review lessons'
                      : isEnrolled
                        ? 'Continue learning'
                        : 'Start course'
                    : 'Sign in to start'
                }
                onOpenCurriculum={() => {
                  if (!isAuthenticated) {
                    navigate('/signin');
                    return;
                  }
                  setSelectedTab('lessons');
                }}
                isEnrolled={isEnrolled}
                // Hide Enroll for draft courses (learners can't enroll
                // in unpublished work) and for unauthenticated visitors
                // (the persistent sign-in CTA above handles that path).
                onEnroll={
                  isAuthenticated && !displayCourse?.isDraft
                    ? handleEnroll
                    : undefined
                }
                // Admins get an "Assign instructor →" shortcut on
                // the empty hero + tab states. Plain learners don't.
                editLink={
                  courseManagement.canEdit && (displayCourse as any)?.slug
                    ? `/courses/${(displayCourse as any).slug}/edit`
                    : undefined
                }
              />
            </Box>
          )}

          <Box
            sx={{
              width: '95%',
              mx: 'auto',
              mt: 3,
              display: selectedTab === 'overview' ? 'none' : 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'center',
              maxWidth: '1500px',
              gap: 5,
              pb: 4,
            }}
          >
            {/* External Course Section - Show for external courses on lessons tab */}
            {selectedTab === 'lessons' && isExternal && (
              <ExternalCourseSection
                eventDate={eventDate}
                isExternalCompleted={isExternalCompleted}
                isEnrolled={isEnrolled}
                completedAt={displayCourse?.currentUserEnrollment?.completedAt}
                learnerVerificationActive={learnerVerificationActive}
                externalUrl={externalUrl}
                externalLoading={externalLoading}
                onEnroll={handleEnroll}
                onOpenExternalUrl={handleOpenExternalUrl}
                onOpenExternalEvent={handleOpenExternalEvent}
                verificationPanel={
                  !isExternalCompleted && verification && learnerVerificationActive ? (
                    <EventVerificationPanel
                      verification={verification}
                      codeAttempt={codeAttempt}
                      codeError={codeError}
                      attestationChecked={attestationChecked}
                      verifyLoading={verifyLoading}
                      onCodeChange={(value) => {
                        setCodeAttempt(value);
                        if (codeError) setCodeError(null);
                      }}
                      onAttestationChange={setAttestationChecked}
                      onVerify={handleVerifyAttendance}
                    />
                  ) : undefined
                }
              />
            )}

            {/* Learning Path Section - Show for learning path courses on lessons tab */}
            {selectedTab === 'lessons' && isLearningPath && (
              <LearningPathSection
                hasLessons={lessonManagement.lessons.length > 0}
                lessonSidebar={lessonSidebar}
                pathCourses={pathCourses}
                completedPathCourses={completedPathCourses}
                totalPathCourses={totalPathCourses}
                enrollmentStatus={enrollmentStatus}
                progressPercentage={(displayCourse as any)?.currentUserEnrollment?.progressPercentage}
                curriculumLocked={curriculumLocked}
                learningPathSlug={displayCourse?.slug}
                shouldShowLessonViewer={shouldShowLessonViewer}
                hasSelectedLesson={!!lessonManagement.selectedLesson}
                selectedLessonLocked={selectedLessonLocked}
                lockedLessonPanel={lockedLessonPanel}
                lessonViewer={lessonViewer}
                courseTitle={displayCourse?.title}
                courseDescription={displayCourse?.description}
                isDraft={displayCourse?.isDraft}
                onEnroll={handleEnroll}
              />
            )}

            {/* Standard Lessons Section - Hide for external courses and learning paths */}
            {selectedTab === 'lessons' && !isExternal && !isLearningPath && (
              <StandardLessonsSection
                lessonsEmpty={lessonManagement.lessons.length === 0}
                canEdit={courseManagement.canEdit}
                lessonSidebar={lessonSidebar}
                shouldShowLessonViewer={shouldShowLessonViewer}
                hasSelectedLesson={!!lessonManagement.selectedLesson}
                selectedLessonLocked={selectedLessonLocked}
                lockedLessonPanel={lockedLessonPanel}
                lessonViewer={lessonViewer}
                isDraft={displayCourse?.isDraft}
                isAuthenticated={isAuthenticated}
                onEnroll={handleEnroll}
                onSignIn={() => navigate('/signin')}
              />
            )}

            {/* Assignment Setup Section */}
            {courseManagement.canAssign && !displayCourse?.isDraft && selectedTab === 'assignment-setup' && (
              <Box sx={{ width: '100%' }}>
                <AssignmentSetup courseId={effectiveCourseId!} />
              </Box>
            )}

            {/* Assignments (Student Progress) Section */}
            {courseManagement.canAssign && !displayCourse?.isDraft && selectedTab === 'assignments' && (
              <Box sx={{ width: '100%' }}>
                <UserAssignmentsTable courseId={effectiveCourseId!} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Floating Action Button - Speed dial for adding lessons or sections (not for external courses).
            Learning paths can own lessons directly and stack them above the path course list. */}
        {courseManagement.canEdit && selectedTab === 'lessons' && !isExternal && (
          <FloatingActionButton
            actions={[
              {
                icon: <CreateNewFolderIcon sx={{ fontSize: '32px' }} />,
                name: 'Add Section',
                onClick: moduleManagement.openAddModuleDialog,
              },
              {
                icon: <BookmarkIcon sx={{ fontSize: '32px' }} />,
                name: 'Add Lesson',
                onClick: lessonManagement.openAddDialog,
              },
            ]}
          />
        )}

        {/* Dialogs */}
        <EditLessonDialog
          open={lessonManagement.showDialog}
          onSave={lessonManagement.saveLesson}
          onClose={lessonManagement.closeDialog}
          lessonToEdit={lessonManagement.lessonToEdit}
          modules={moduleManagement.modules}
        />

        <ConfirmDialog
          open={lessonManagement.confirmOpen}
          title="Delete Lesson"
          content="Are you sure you want to delete this lesson?"
          onConfirm={lessonManagement.deleteLesson}
          onCancel={lessonManagement.cancelDelete}
        />

        <EditModuleDialog
          open={moduleManagement.showModuleDialog}
          onSave={moduleManagement.saveModule}
          onClose={moduleManagement.closeModuleDialog}
          moduleToEdit={moduleManagement.moduleToEdit}
        />

        <ConfirmDialog
          open={moduleManagement.confirmModuleOpen}
          title="Delete Section"
          content="Are you sure you want to delete this section? Lessons in this section will become ungrouped."
          onConfirm={moduleManagement.deleteModule}
          onCancel={moduleManagement.cancelDeleteModule}
        />
      </Box>
  );
}
